import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import GradientProgress from '../components/UIKit/GradientProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PendingIcon from '@mui/icons-material/Pending';

const TestResultDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation(['tests', 'common']);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResultDetail = async () => {
      try {
        setLoading(true);
        const response = await testsAPI.getResultDetail(id);
        setResult(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Ошибка при загрузке результата теста:', error);
        setError(t('tests:results.error_loading_tests'));
        setLoading(false);
      }
    };

    fetchResultDetail();
  }, [id, t]);

  // Возврат к списку результатов
  const handleBackToResults = () => {
    navigate('/test-results');
  };

  // Пройти тест заново
  const handleRetakeTest = async () => {
    if (result && result.test) {
      try {
        const testInfo = await testsAPI.getTestInfo(result.test);
        const { courseSlug, moduleSlug, lessonSlug } = testInfo.data;
        const path = lessonSlug
          ? `/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${result.test}`
          : `/courses/${courseSlug}/modules/${moduleSlug}/tests/${result.test}`;
        navigate(path);
      } catch (error) {
        toast.error(t('tests:errors.startTestFailed'));
      }
    }
  };

  const getStatusIcon = () => {
    if (!result.completed) {
      return <PendingIcon color="warning" sx={{ mr: 1 }} />;
    }
    if (result.hasPendingReview) {
      return <PendingIcon color="warning" sx={{ mr: 1 }} />;
    }
    return result.passed ? (
      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
    ) : (
      <ErrorIcon color="error" sx={{ mr: 1 }} />
    );
  };

  const renderAnswers = () => {
    if (!result || !result.answers || result.answers.length === 0) {
      return <Alert severity="info">{t('tests:no_answers_info')}</Alert>;
    }

    return (
      <Box sx={{ mt: 3 }}>
        {result.answers.map((answer, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              borderRadius: '12px',
              borderColor: answer.reviewStatus === 'pending'
                ? theme.palette.warning.main
                : answer.isCorrect
                  ? theme.palette.success.main
                  : theme.palette.error.main,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('tests:question')} {index + 1}
                  </Typography>
                  <Chip
                    icon={
                      answer.reviewStatus === 'pending' ? (
                        <PendingIcon color="warning" />
                      ) : answer.isCorrect ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )
                    }
                    label={
                      answer.reviewStatus === 'pending'
                        ? t('tests:results.pending_review', 'Очікує перевірки')
                        : answer.isCorrect
                          ? t('tests:results.correctAnswers')
                          : t('tests:results.incorrect_answers')
                    }
                    color={answer.reviewStatus === 'pending' ? 'warning' : answer.isCorrect ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {answer.question}
                </Typography>
              </Box>
            </Box>

            {answer.image && (
              <Box sx={{ mb: 1, textAlign: 'center' }}>
                <img
                  src={answer.image}
                  alt={t('tests:question')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '240px',
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Card
                sx={{
                  flexGrow: 1,
                  minWidth: '250px',
                  backgroundColor: answer.reviewStatus === 'pending'
                    ? `${theme.palette.warning.main}10`
                    : answer.isCorrect
                      ? `${theme.palette.success.main}10`
                      : `${theme.palette.error.main}10`,
                  borderRadius: '8px',
                  p: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('tests:results.your_answer')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: answer.reviewStatus === 'pending'
                      ? theme.palette.warning.main
                      : answer.isCorrect
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                    fontWeight: 'medium',
                    mt: 0.5,
                  }}
                >
                  {answer.userAnswer || t('tests:no_answer')}
                </Typography>
              </Card>

              {answer.questionType !== 'open-answer' && !answer.isCorrect && answer.reviewStatus !== 'pending' && answer.correctAnswer && (
                <Card
                  sx={{
                    flexGrow: 1,
                    minWidth: '250px',
                    backgroundColor: `${theme.palette.success.main}10`,
                    borderRadius: '8px',
                    p: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t('tests:correct_answer')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.success.main, fontWeight: 'medium', mt: 0.5 }}
                  >
                    {answer.correctAnswer}
                  </Typography>
                </Card>
              )}
            </Box>

            {answer.explanation && (
              <Card
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: `${theme.palette.info.main}10`,
                  borderRadius: '8px',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('tests:explanation')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {answer.explanation}
                </Typography>
              </Card>
            )}

{answer.reviewFeedback && answer.reviewedBy && (
              <Card
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: answer.reviewStatus === 'approved'
                    ? `${theme.palette.success.main}10`
                    : `${theme.palette.error.main}10`,
                  borderRadius: '8px',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('tests:results.teacherComment')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {answer.reviewFeedback}
                </Typography>
              </Card>
            )}
          </Paper>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!result) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {t('tests:results.error_loading_tests')}
      </Alert>
    );
  }

  const pendingCount = (result.answers || []).filter(a => a.reviewStatus === 'pending').length;
  const incorrectCount = result.totalQuestions - result.correctAnswers - pendingCount;

  return (
    <Container sx={{ py: 4 }}>
      <Button
        variant="neutral"
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToResults}
        sx={{ mb: 3 }}
      >
        {t('tests:results.back_to_results')}
      </Button>

      {result.hasPendingReview && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
          {t('tests:results.pending_review_notice', 'Деякі відповіді очікують перевірки вчителем. Фінальний результат буде оновлено після перевірки.')}
        </Alert>
      )}

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '16px',
        }}
        elevation={0}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getStatusIcon()}
            <Typography
              variant="h4"
              sx={{
                fontSize: {
                  xs: '1.5rem', // smaller size for mobile
                  sm: '2rem', // medium size for tablets
                  md: '2.125rem', // default h4 size for desktop
                },
                lineHeight: {
                  xs: 1.3,
                  sm: 1.235,
                },
                wordBreak: 'break-word', // prevents overflow on small screens
              }}
            >
              {result.testName}
            </Typography>
          </Box>
          <Button variant="contained" onClick={handleRetakeTest} size="small">
            {t('tests:results.try_again')}
          </Button>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {result.completed
            ? `${t('tests:results.passed')}: ${new Date(
                result.completedAt
              ).toLocaleString()}`
            : t('tests:results.not_completed')}
        </Typography>

        {result.completed ? (
          <>
            <Grid container spacing={2}>
              {/* Score Section */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {`${Math.round(result.score)}%`}
                  </Typography>
                  <GradientProgress
                    value={result.score}
                    height={6}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    {t('tests:results.overall_score')}
                  </Typography>
                </Card>
              </Grid>

              {/* Correct/Incorrect Section */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 2,
                    height: '100%',
                    backgroundColor: theme.palette.background.default,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('tests:results.correctAnswers')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">{result.correctAnswers}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('tests:results.incorrect_answers')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">{incorrectCount}</Typography>
                    </Box>
                  </Box>
                  {pendingCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('tests:results.pending_review')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PendingIcon color="warning" sx={{ mr: 1 }} />
                        <Typography variant="h6">{pendingCount}</Typography>
                      </Box>
                    </Box>
                  )}
                  {result.totalPoints > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('tests:results.earned_points')}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {result.earnedPoints ?? 0} / {result.totalPoints}
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Time Section */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <AccessTimeIcon
                    color="primary"
                    sx={{ fontSize: 40, mb: 1 }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {result.timeSpent
                      ? `${Math.floor(result.timeSpent / 60)}:${String(
                          result.timeSpent % 60
                        ).padStart(2, '0')}`
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('tests:results.time_taken')}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {result.feedback && (
              <Card
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Typography variant="body1" gutterBottom fontWeight="medium">
                  {t('tests:results.comment')}:
                </Typography>
                <Typography variant="body1">{result.feedback}</Typography>
              </Card>
            )}
          </>
        ) : (
          <Card
            sx={{
              p: 4,
              backgroundColor: theme.palette.background.default,
              textAlign: 'center',
            }}
          >
            <PendingIcon
              sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              {t('tests:results.test_not_completed')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('tests:results.test_not_completed_message')}
            </Typography>
            <Button variant="contained" onClick={handleRetakeTest}>
              {t('tests:results.start_test')}
            </Button>
          </Card>
        )}
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        {t('tests:results.answers_to_questions')}
      </Typography>

      {renderAnswers()}
    </Container>
  );
};

export default TestResultDetail;
